import React, { useState } from 'react';
import times from 'lodash/times';
import { useIsMutating } from 'react-query';
import { useActionSteps } from './useActionSteps';
import { ActionStepsHeader } from '../components/ActionStepsHeader/ActionStepsHeader';
import { TaskItem } from '../components/TaskItem/TaskItem';
import { TaskItemLoading } from '../components/TaskItem/TaskItem.loading';
import { MAX_TASKS } from '../Tasks.constants';
import { useIsCurrentPlanSelected } from '../../../hooks/useIsCurrentPlanSelected';
import { EmptyList } from '../components/EmptyList/EmptyList';
import { TaskItemFooter } from '../components/TaskItemFooter/TaskItemFooter';
import { Card } from '../../../../../CommonComponents/OPL/Card/Card';
import { RESET_ACTION_STEPS_MK } from '../api/useResetActionStepMutation';
import { useIsPdf } from '../../../../hooks/useIsPdf';

interface ActionStepsProps {
  goalId?: string;
  debtType?: number;
}

export const ActionSteps = ({ goalId, debtType }: ActionStepsProps) => {
  const { isCurrentPlanSelected } = useIsCurrentPlanSelected();

  if (isCurrentPlanSelected) {
    return null;
  }

  return (
    <Card p="16" height="100%">
      <ActionStepsHeader debtType={debtType} />

      <ActionStepsContent goalId={goalId} />
    </Card>
  );
};

const ActionStepsContent = ({ goalId }: ActionStepsProps) => {
  const isPrint = useIsPdf();
  const [showTotalList, setShowTotalList] = useState(isPrint);

  const { data, isLoading } = useActionSteps(goalId);

  const isResetting = useIsMutating({ mutationKey: RESET_ACTION_STEPS_MK }) > 0;

  if (isLoading || isResetting) {
    return (
      <>
        {times(4).map(i => (
          <TaskItemLoading key={i} icon={i} />
        ))}
      </>
    );
  }

  if (!data) {
    return null;
  }

  if (data.actionSteps.length === 0) {
    return <EmptyList />;
  }

  return (
    <>
      {data.actionSteps
        .filter((task, index) => showTotalList || index < MAX_TASKS)
        .sort((a, b) => {
          if (a.DueDate === b.DueDate) {
            return new Date(a.CreatedOn).getTime() - new Date(b.CreatedOn).getTime();
          }

          return new Date(a.DueDate ?? 0).getTime() - new Date(b.DueDate ?? 0).getTime();
        })
        .map(actionStep => (
          <TaskItem
            key={actionStep.Id}
            {...actionStep}
            pulseCheckCategory={data.goal.PulseCheckCategory}
            advisorAssignees={data.advisorAssignees}
            showAssetBucketing={data.showAssetBucketing}
            availableAssetBuckets={data.availableAssetBuckets}
          />
        ))}

      {data.actionSteps.length > MAX_TASKS && !isPrint && (
        <TaskItemFooter setShowTotalList={setShowTotalList} showTotalList={showTotalList} />
      )}
    </>
  );
};
